import React, { useState, useMemo } from 'react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { ProjectLog, WBSTask, LogType, LogStatus, ScopeClassification, AuditLogEntry, EVMSnapshot } from '../types.ts';
// Added HelpCircle to the imports from lucide-react
import {
  Plus, Trash2, Clock, Sparkles, Loader2, ShieldCheck,
  AlertCircle, FileText, Gavel,
  CheckCircle2, ChevronRight,
  Zap, Hammer, AlertTriangle, MapPin, History, Landmark, Cpu, HelpCircle
} from 'lucide-react';
import { Modal } from './Modal.tsx';
import { forensicLogAudit, generateCONarrative } from '../services/geminiService.ts';
import { useToast } from '../hooks/useToast.ts';
import { formatCurrency, calculateFinancialMetrics } from '../services/financialService.ts';

export const LogsPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const project = useStore(() => api.projects.get(projectId), [projectId], 'projects');
  const wbs = useStore<WBSTask[]>(() => api.wbsTasks.list(t => t.projectId === projectId), [projectId], 'wbsTasks') || [];
  const logs = useStore<ProjectLog[]>(() => api.projectLogs.list(l => l.projectId === projectId), [projectId], 'projectLogs') || [];
  const auditLogs = useStore<AuditLogEntry[]>(() => api.auditLogs.list(a => a.projectId === projectId), [projectId], 'auditLogs') || [];
  const evm = useStore<EVMSnapshot[]>(() => api.evm.list(e => e.projectId === projectId), [projectId], 'evm') || [];

  const { addToast } = useToast();

  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isAuditingBatch, setIsAuditingBatch] = useState(false);
  const [isDraftingCO, setIsDraftingCO] = useState(false);
  const [isOverriding, setIsOverriding] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [targetClassification, setTargetClassification] = useState<ScopeClassification | null>(null);
  const [showTimeline, setShowTimeline] = useState(false);

  const [newLog, setNewLog] = useState<Partial<ProjectLog>>({
    content: '',
    tags: [],
    logType: 'Issue',
    location: '',
    status: 'New'
  });
  const [coDraft, setCoDraft] = useState<any>(null);

  const selectedLog = useMemo(() => logs.find(l => l.id === selectedLogId), [logs, selectedLogId]);

  const forensicTimeline = useMemo(() => {
    if (!selectedLogId) return [] as AuditLogEntry[];
    return (auditLogs as AuditLogEntry[])
      .filter(a => a.entityType === 'ProjectLog' && a.entityId === selectedLogId)
      .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime());
  }, [auditLogs, selectedLogId]);

  const marginStatus = useMemo(() => {
    if (!evm.length || !project) return 'Green';
    const latest = evm[evm.length - 1];
    const metrics = calculateFinancialMetrics(latest, project.contractAmount);
    if (metrics.variance < -100000) return 'Red';
    if (metrics.variance < 0) return 'Amber';
    return 'Green';
  }, [evm, project]);

  const logsByDate = useMemo(() => {
    const groups: Record<string, ProjectLog[]> = {};
    [...logs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).forEach(log => {
      const d = new Date(log.date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
      if (!groups[d]) groups[d] = [];
      groups[d].push(log);
    });
    return groups;
  }, [logs]);

  const auditTodayLogs = async () => {
    if (!project) return;
    const targets = logs.filter(l => l.status === 'New');
    if (!targets.length) {
      addToast({ type: 'info', title: 'Audit Complete', message: 'No new entries require assessment.' });
      return;
    }
    setIsAuditingBatch(true);
    try {
      for (const log of targets) {
        const result = await forensicLogAudit(project, wbs, log.content);
        const status: LogStatus = result.scopeClassification === 'OutOfScopeLikely' ? 'FlaggedForCO' : 'Reviewed';
        await api.projectLogs.update(log.id!, { auditResult: result, status });
        await api.logAction(projectId, 'AI_FORENSIC_ASSESSMENT', 'ProjectLog', log.id!, `AI classified as ${result.scopeClassification} (${(result.confidenceScore * 100).toFixed(0)}% confidence). ${result.summary}`);
      }
      addToast({ type: 'success', title: 'Forensic Audit Complete', message: `Analyzed ${targets.length} entries. Check flags.` });
    } catch (err) {
      addToast({ type: 'error', title: 'Engine Error', message: 'Forensic node failure. Check API credits.' });
    } finally {
      setIsAuditingBatch(false);
    }
  };

  const saveLog = async () => {
    if (!newLog.content) return;
    const id = await api.projectLogs.add({
      projectId,
      date: new Date().toISOString(),
      author: 'Owner Admin',
      content: newLog.content,
      logType: newLog.logType || 'Issue',
      location: newLog.location || '',
      tags: newLog.tags || [],
      status: 'New'
    } as ProjectLog);
    await api.logAction(projectId, 'LOG_REGISTERED', 'ProjectLog', id, 'Daily field observation committed to ledger.');
    setIsAdding(false);
    setNewLog({ content: '', tags: [], logType: 'Issue', location: '' });
  };

  const handleCreateCODraft = async () => {
    if (!project || !selectedLog || !selectedLog.auditResult) return;
    setIsDraftingCO(true);
    try {
      const wbsMatches = selectedLog.auditResult.wbsMatches || [];
      const wbsContext = wbsMatches.map((m) => {
        const task = wbs.find(t => t.id === m.id);
        return task ? `${task.wbsCode}: ${task.title}` : '';
      }).filter(Boolean).join(', ');

      const narrative = await generateCONarrative(project, selectedLog.content, selectedLog.auditResult, wbsContext);
      setCoDraft({ ...narrative, logId: selectedLog.id });
    } catch (err) {
      addToast({ type: 'error', title: 'Drafting Failed', message: 'Could not generate forensic claim justification.' });
    } finally {
      setIsDraftingCO(false);
    }
  };

  const saveCOFromDraft = async () => {
    if (!coDraft || !project) return;
    const coId = await api.changeOrders.add({
      projectId,
      coNumber: `CO-FOR-${Date.now().toString().slice(-4)}`,
      title: coDraft.title,
      amount: 0,
      status: 'Draft',
      date: new Date().toISOString().split('T')[0],
      description: coDraft.summary,
      communicationLog: `Forensic claim generated from site log #${selectedLog!.id}`,
      accountabilitySummary: coDraft.narrative,
      linkedLogId: coDraft.logId
    });
    await api.projectLogs.update(coDraft.logId, { status: 'LinkedToCO' });
    await api.logAction(projectId, 'CLAIM_DRAFTED', 'ChangeOrder', coId, `Drafted formal claim based on forensic site log #${coDraft.logId}`);
    addToast({ type: 'success', title: 'Claim Drafted', message: 'Move to Claims tab to finalize.' });
    setCoDraft(null);
  };

  const commitOverride = async () => {
    if (!selectedLog || !targetClassification || !overrideReason) return;
    const oldClass = selectedLog.auditResult?.scopeClassification || 'None';
    const currentAudit = selectedLog.auditResult || {
      scopeClassification: targetClassification,
      causeType: 'Other',
      recommendedAction: targetClassification === 'OutOfScopeLikely' ? 'DraftCO' : 'NoAction',
      summary: overrideReason,
      notesForPM: [],
      confidenceScore: 1.0,
      wbsMatches: []
    };

    await api.projectLogs.update(selectedLog.id!, {
      status: targetClassification === 'OutOfScopeLikely' ? 'FlaggedForCO' : 'Reviewed',
      auditResult: {
        ...currentAudit,
        scopeClassification: targetClassification,
        recommendedAction: targetClassification === 'OutOfScopeLikely' ? 'DraftCO' : 'NoAction'
      }
    });
    await api.logAction(projectId, 'PM_DECISION_OVERRIDE', 'ProjectLog', selectedLog.id!, `Manual Override: ${oldClass} → ${targetClassification}. Reason: ${overrideReason}`);
    setIsOverriding(false);
    setOverrideReason('');
    setTargetClassification(null);
  };

  const getLogIcon = (type: LogType) => {
    switch (type) {
      case 'Issue': return <AlertCircle size={16} className="text-amber-500" />;
      case 'ExtraWork': return <Hammer size={16} className="text-emerald-500" />;
      case 'Delay': return <Clock size={16} className="text-amber-500" />;
      case 'Discovery': return <Landmark size={16} className="text-indigo-500" />;
      case 'Clarification': return <HelpCircle size={16} className="text-zinc-400" />;
      case 'Safety': return <ShieldCheck size={16} className="text-rose-600" />;
      default: return <FileText size={16} className="text-zinc-400" />;
    }
  };

  const getScopeBadge = (classification?: ScopeClassification) => {
    switch (classification) {
      case 'InScope': return 'text-emerald-500 bg-emerald-50 border-emerald-100';
      case 'OutOfScopeLikely': return 'text-amber-500 bg-amber-50 border-amber-100';
      case 'Ambiguous': return 'text-amber-500 bg-amber-50 border-amber-100';
      default: return 'text-zinc-400 bg-zinc-50 border-zinc-100';
    }
  };

  return (
    <div className="flex flex-col h-[800px] bg-white border border-zinc-200 rounded-[3rem] shadow-2xl overflow-hidden animate-in fade-in duration-500">
      <div className="px-10 py-8 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-black text-black tracking-tight uppercase">Forensic Site Registry</h2>
            <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${marginStatus === 'Red' ? 'bg-amber-500 text-white border-amber-500' :
                marginStatus === 'Amber' ? 'bg-amber-500 text-white border-amber-500' : 'bg-emerald-500 text-white border-emerald-500'
              }`}>Margin Status: {marginStatus}</span>
          </div>
          <div className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mt-1 flex items-center gap-2">
            <span>Job Magnitude: {formatCurrency(project?.contractAmount || 0)}</span>
            <div className="w-1 h-1 bg-zinc-300 rounded-full"></div>
            <span>End-of-Day Audit Protocol Active</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setIsAdding(true)} className="px-6 py-3 bg-white border border-zinc-200 text-black text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-zinc-50 transition-all flex items-center gap-2 shadow-sm">
            <Plus size={14} /> New Field Entry
          </button>
          <button
            onClick={auditTodayLogs}
            disabled={isAuditingBatch}
            className="px-6 py-3 bg-black text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-xl shadow-black/20 ai-shimmer"
          >
            {isAuditingBatch ? <Loader2 size={14} className="animate-spin" /> : <Gavel size={14} />}
            Perform Forensic Batch Audit
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-[380px] border-r border-zinc-100 overflow-y-auto custom-scrollbar bg-white">
          {/* Explicitly cast Object.entries to resolve 'unknown' type inference error for dayLogs */}
          {(Object.entries(logsByDate) as [string, ProjectLog[]][]).map(([date, dayLogs]) => (
            <div key={date}>
              <div className="sticky top-0 bg-zinc-50/90 backdrop-blur px-8 py-4 border-b border-zinc-100 z-10 flex justify-between items-center">
                <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{date}</span>
                <span className="text-[8px] font-black text-zinc-300 uppercase">{dayLogs.length} Entries</span>
              </div>
              <div className="divide-y divide-zinc-50">
                {dayLogs.map(log => (
                  <button
                    key={log.id}
                    onClick={() => { setSelectedLogId(log.id!); setShowTimeline(false); }}
                    className={`w-full text-left p-8 transition-all hover:bg-zinc-50 relative group ${selectedLogId === log.id ? 'bg-zinc-50' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        {getLogIcon(log.logType)}
                        <span className="text-xs font-black text-black line-clamp-1">{log.content.split('\n')[0]}</span>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {log.status === 'FlaggedForCO' && <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shadow-sm shadow-amber-500/50"></div>}
                        {log.status === 'New' && <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-sm shadow-indigo-500/20"></div>}
                        {log.status === 'LinkedToCO' && <CheckCircle2 size={12} className="text-emerald-500" />}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">{log.author} • {log.location || 'Site Area'}</span>
                      <ChevronRight size={14} className={`text-zinc-200 transition-transform ${selectedLogId === log.id ? 'translate-x-1 text-black' : ''}`} />
                    </div>
                    {selectedLogId === log.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-black"></div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {logs.length === 0 && (
            <div className="p-20 text-center opacity-30">
              <Landmark size={48} className="mx-auto mb-4 text-zinc-200" />
              <p className="text-[10px] font-black uppercase tracking-widest">No site records committed.</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-12 bg-zinc-50/30">
          {!selectedLog ? (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-sm mx-auto opacity-40">
              <div className="w-20 h-20 bg-white border border-zinc-100 rounded-3xl flex items-center justify-center mb-6 shadow-sm"><Zap size={32} className="text-zinc-200" /></div>
              <h3 className="text-lg font-black text-black tracking-tight uppercase">Forensic Command</h3>
              <p className="text-xs font-medium text-zinc-500 leading-relaxed mt-2">Select a field observation to analyze contract compliance or capture extra work.</p>
            </div>
          ) : (
            <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-white rounded-[2.5rem] border border-zinc-200 p-10 shadow-sm">
                <div className="flex justify-between items-center mb-8 border-b border-zinc-50 pb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-zinc-50 rounded-2xl text-black border border-zinc-100 shadow-inner">
                      {getLogIcon(selectedLog.logType)}
                    </div>
                    <div>
                      <h3 className="font-black text-xl text-black uppercase tracking-tight">Observation Detail</h3>
                      <div className="text-[9px] font-black uppercase text-zinc-400 tracking-widest mt-1">
                        Authored by {selectedLog.author} • Location: {selectedLog.location || 'Unspecified'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowTimeline(!showTimeline)}
                      className={`p-3 rounded-xl transition-all ${showTimeline ? 'bg-black text-white shadow-xl' : 'text-zinc-300 hover:text-black hover:bg-zinc-100'}`}
                      title="View Decision Timeline"
                    >
                      <History size={20} />
                    </button>
                    <button onClick={() => api.projectLogs.delete(selectedLog.id!)} className="p-3 text-zinc-200 hover:text-amber-500 transition-colors"><Trash2 size={20} /></button>
                  </div>
                </div>

                {showTimeline ? (
                  <div className="space-y-4 animate-in fade-in">
                    <h4 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-2">Forensic Timeline (The Golden Thread)</h4>
                    <div className="bg-zinc-900 rounded-3xl p-8 divide-y divide-zinc-800">
                      {forensicTimeline.map(item => (
                        <div key={item.id} className="py-6 first:pt-0 last:pb-0">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">{item.action.replace(/_/g, ' ')}</span>
                            <span className="text-[8px] font-black text-zinc-600 uppercase">{new Date(item.createdAt!).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-zinc-400 font-bold leading-relaxed">"{item.details}"</p>
                          <span className="text-[8px] font-black text-zinc-700 uppercase mt-2 block">Authorized by: {item.createdBy}</span>
                        </div>
                      ))}
                      {forensicTimeline.length === 0 && <p className="text-[10px] text-zinc-600 font-black uppercase text-center py-8">Baseline record: No interactions logged yet.</p>}
                    </div>
                  </div>
                ) : (
                  <div className="p-10 bg-zinc-50 rounded-[2rem] border border-zinc-100 text-base text-zinc-700 leading-relaxed font-normal whitespace-pre-wrap italic">
                    "{selectedLog.content}"
                  </div>
                )}
              </div>

              <div className="bg-white rounded-[3rem] border border-zinc-200 p-10 shadow-sm relative overflow-hidden group">
                <div className="space-y-10">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-100 text-black shadow-inner group-hover:scale-110 transition-transform">
                        <ShieldCheck size={24} className="text-emerald-500" />
                      </div>
                      <div>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400">Intelligence Synthesis</h4>
                        <div className="text-xl font-black text-black uppercase tracking-tight mt-1">Forensic Brief</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <div className="px-6 py-3 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center gap-4">
                        <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest">Confidence Index</span>
                        <span className="text-sm font-black text-emerald-600">{(selectedLog.auditResult?.confidenceScore ?? 0) * 100}%</span>
                      </div>
                      <div className="px-6 py-3 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Cpu size={12} className="text-zinc-300" />
                          <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest">Classification</span>
                        </div>
                        <span className={`text-sm font-black uppercase tracking-widest ${getScopeBadge(selectedLog.auditResult?.scopeClassification).split(' ')[0]}`}>
                          {selectedLog.auditResult?.scopeClassification || 'Awaiting'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-10 border-t border-zinc-50">
                    <div className="flex justify-between items-center mb-6">
                      <span className="text-[9px] font-black uppercase text-zinc-300 tracking-[0.2em]">Forensic Recovery Path</span>
                    </div>
                    <div className="min-h-[100px] space-y-8">
                      <p className="text-xl font-medium text-zinc-700 leading-[1.6] italic max-w-5xl">
                        {selectedLog.auditResult?.summary || "Awaiting synchronization of forensic cost indicators from WBS and site log data pools..."}
                      </p>

                      {selectedLog.auditResult && (
                        <div className="grid md:grid-cols-2 gap-10">
                          <div>
                            <h5 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-4">WBS Compliance Logic</h5>
                            <div className="space-y-3">
                              {selectedLog.auditResult.wbsMatches?.map((m: any) => (
                                <div key={m.id} className="flex items-center gap-4 p-4 bg-zinc-50 rounded-2xl border border-zinc-100/50">
                                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                  <div className="flex-1">
                                    <div className="text-[10px] font-black text-black uppercase">{wbs.find(t => t.id === m.id)?.title || 'Task Registry'}</div>
                                    <div className="text-[10px] text-zinc-400 italic mt-1 leading-snug">{m.rationale}</div>
                                  </div>
                                </div>
                              ))}
                              {(!selectedLog.auditResult.wbsMatches || selectedLog.auditResult.wbsMatches.length === 0) && <p className="text-xs text-zinc-400 font-bold italic">No baseline overlaps identified.</p>}
                            </div>
                          </div>
                          <div>
                            <h5 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-4">Strategic Action</h5>
                            <div className="flex gap-4">
                              {selectedLog.auditResult.recommendedAction === 'DraftCO' && (
                                <button
                                  onClick={handleCreateCODraft}
                                  disabled={isDraftingCO}
                                  className="px-8 py-4 bg-black text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-xl shadow-black/20 ai-shimmer"
                                >
                                  {isDraftingCO ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={14} />}
                                  Generate Claim Draft
                                </button>
                              )}
                              <button onClick={() => { setTargetClassification(null); setIsOverriding(true); }} className="px-6 py-4 border border-zinc-100 rounded-xl text-[9px] font-black uppercase tracking-widest text-zinc-400 hover:text-black hover:border-black transition-all">Manual Override</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isOverriding} onClose={() => setIsOverriding(false)} title="Decision Override Protocol" maxWidth="sm">
        <div className="space-y-8">
          <div className="p-6 bg-amber-50 rounded-[1.5rem] border border-amber-100 flex gap-4">
            <AlertTriangle className="text-amber-600 shrink-0" size={24} />
            <p className="text-xs font-bold text-amber-900 leading-relaxed">Overrides are committed to the Immutable Audit Ledger for regional compliance. Professional rationale is required.</p>
          </div>

          {!targetClassification ? (
            <div className="space-y-3">
              <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-4">Set New Scope Classification</label>
              {['InScope', 'OutOfScopeLikely', 'Ambiguous'].map((c) => (
                <button
                  key={c}
                  onClick={() => setTargetClassification(c as ScopeClassification)}
                  className="w-full py-4 border border-zinc-100 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-black transition-all text-center bg-zinc-50"
                >
                  {c.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-6 animate-in slide-in-from-right-2">
              <div>
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Override Rationale*</label>
                <textarea
                  className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-bold text-sm h-32 focus:bg-white outline-none"
                  placeholder="e.g. Work confirmed as owner-directed during site walk on Friday..."
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.target.value)}
                />
              </div>
              <div className="flex gap-4">
                <button onClick={() => setTargetClassification(null)} className="flex-1 py-4 border border-zinc-100 rounded-xl text-[9px] font-black uppercase tracking-widest text-zinc-400">Back</button>
                <button
                  onClick={commitOverride}
                  disabled={!overrideReason}
                  className="flex-[2] py-4 bg-black text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-xl disabled:opacity-30"
                >
                  Commit to Golden Thread
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {coDraft && (
        <Modal isOpen={!!coDraft} onClose={() => setCoDraft(null)} title="Forensic Change Order Draft" maxWidth="lg">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1 space-y-8 bg-zinc-50/50 p-8 rounded-[2rem] border border-zinc-100">
              <div>
                <h4 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-4">Audit Provenance</h4>
                <div className="space-y-4">
                  <div>
                    <span className="text-[8px] font-black text-zinc-400 uppercase block">Log Reference</span>
                    <span className="text-[10px] font-black text-black">Observation #{selectedLog?.id}</span>
                  </div>
                  <div>
                    <span className="text-[8px] font-black text-zinc-400 uppercase block">Owner Magnitude</span>
                    <span className="text-[10px] font-black text-black">{formatCurrency(project?.contractAmount || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-6">
                <div>
                  <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Claim Title*</label>
                  <input className="w-full border border-zinc-100 bg-zinc-50 p-4 rounded-xl font-black text-sm focus:bg-white outline-none" value={coDraft.title} onChange={e => setCoDraft({ ...coDraft, title: e.target.value })} />
                </div>
                <div>
                  <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Technical Justification (The Narrative)*</label>
                  <textarea
                    className="w-full border border-zinc-100 bg-zinc-50 p-6 rounded-3xl font-bold text-sm h-64 focus:bg-white outline-none leading-relaxed"
                    value={coDraft.narrative}
                    onChange={e => setCoDraft({ ...coDraft, narrative: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-4 border-t border-zinc-100 pt-8">
                <button onClick={() => setCoDraft(null)} className="px-8 py-4 text-[9px] font-black uppercase tracking-widest text-zinc-400 hover:text-black transition-colors">Discard Draft</button>
                <button onClick={saveCOFromDraft} className="px-12 py-4 bg-black text-white rounded-2xl text-[9px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-xl shadow-black/20">Finalize as Draft Claim</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <Modal isOpen={isAdding} onClose={() => setIsAdding(false)} title="Register Site Observation" maxWidth="sm">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Entry Type*</label>
              <select
                className="w-full border border-zinc-100 bg-zinc-50 p-4 rounded-xl font-black focus:bg-white outline-none appearance-none"
                value={newLog.logType}
                onChange={e => setNewLog({ ...newLog, logType: e.target.value as LogType })}
              >
                <option value="Issue">Technical Issue</option>
                <option value="ExtraWork">Directed Extra Work</option>
                <option value="Discovery">Forensic/Discovery</option>
                <option value="Delay">Schedule Impact</option>
                <option value="Clarification">RFI Required</option>
                <option value="Safety">Compliance/Safety</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Operational Area</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-300" size={14} />
                <input className="w-full border border-zinc-100 bg-zinc-50 pl-10 pr-4 py-4 rounded-xl font-black focus:bg-white outline-none" placeholder="Zone B, Lvl 3" value={newLog.location} onChange={e => setNewLog({ ...newLog, location: e.target.value })} />
              </div>
            </div>
          </div>
          <div>
            <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Observation Narrative*</label>
            <textarea className="w-full border border-zinc-100 bg-zinc-50 p-5 rounded-2xl font-bold text-sm h-40 focus:bg-white outline-none" placeholder="Relocate ceiling diffusers per owner verbal instruction on site walk..." value={newLog.content} onChange={e => setNewLog({ ...newLog, content: e.target.value })} />
          </div>
          <div className="flex justify-end gap-4 pt-4">
            <button onClick={() => setIsAdding(false)} className="px-8 py-4 text-[9px] font-black uppercase tracking-widest text-zinc-400 hover:text-black transition-colors">Discard</button>
            <button onClick={saveLog} className="px-12 py-4 bg-black text-white rounded-2xl text-[9px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-xl">Commit to Ledger</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};